// "Avisarme / Publicar automáticamente" en el calendario de Instagram —
// ADR-2026-10-28-instagram-publica-el-calendario.
//
// El que publica es el backend: cada minuto mira los posts cuya hora llegó en los calendarios
// automáticos. Acá sólo se elige el modo, y antes de dejar elegir "automático" se mira que la marca
// tenga Instagram conectado CON permiso de publicar; si no, se ofrece conectarlo ahí mismo.
//
// Sólo dev testers (`sePuedePublicarSolo`) mientras Meta no apruebe publicar para cualquiera.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserGroup } from '../../../hooks/useUserGroup';
import {
  abrirVentanaDeInstagram,
  conectarInstagramEnVentana,
  ErrorDeInstagram,
  estadoDeInstagram,
  type EstadoDeInstagram,
} from '../../../lib/instagramConexion';
import { sePuedePublicarSolo } from './avisos';
import { fetchModoPublicacion, setModoPublicacion, type ModoDePublicacion } from './postsApi';
import { ModalConectarInstagram, ToggleDeModo } from './ToggleDeModo';

const SIN_PERMISO_TRAS_CONECTAR =
  'Instagram no le dio a Atlas el permiso para publicar. Probá de nuevo y aceptá “Acceder al contenido y publicarlo”.';

export function PublicarSolo({ lanzamientoId, brandId }: { lanzamientoId: string; brandId: string | null }) {
  const { isDevTester } = useUserGroup();
  const visible = sePuedePublicarSolo(isDevTester);

  const [modo, setModo] = useState<ModoDePublicacion | null>(null);
  const [ig, setIg] = useState<EstadoDeInstagram | null>(null);
  const [modal, setModal] = useState<'sin_cuenta' | 'sin_permiso' | null>(null);
  const [conectando, setConectando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Un cambio de modo a la vez: dos escrituras cruzadas podían dejarlo en automático diciendo "Avisarme". */
  const guardandoRef = useRef(false);

  /** De qué calendario y marca es lo que hay en pantalla: una respuesta vieja no pisa la de ahora. */
  const alcance = `${lanzamientoId}:${brandId ?? ''}`;
  const alcanceRef = useRef(alcance);
  alcanceRef.current = alcance;

  useEffect(() => {
    if (!visible) return;
    const este = alcance;
    setModo(null);
    setIg(null);
    void Promise.all([fetchModoPublicacion(lanzamientoId), estadoDeInstagram(brandId)]).then(([m, e]) => {
      if (alcanceRef.current !== este) return;
      setModo(m);
      setIg(e);
    });
  }, [visible, alcance, lanzamientoId, brandId]);

  const guardarModo = useCallback(
    async (nuevo: ModoDePublicacion) => {
      if (guardandoRef.current) return;
      guardandoRef.current = true;
      const antes = modo;
      setModo(nuevo);
      try {
        const { error: alGuardar } = await setModoPublicacion(lanzamientoId, nuevo);
        if (alGuardar) setModo(antes);
      } finally {
        guardandoRef.current = false;
      }
    },
    [lanzamientoId, modo],
  );

  const cambiar = (nuevo: ModoDePublicacion) => {
    if (nuevo === modo || guardandoRef.current) return;
    // Sin saber cómo está Instagram no se prende; pausar ("Avisarme") se puede igual: es sólo nuestra base.
    if (nuevo === 'automatico' && (!ig || ig.error)) return;
    if (nuevo === 'automatico' && ig) {
      setError(null);
      // El modo NO cambia detrás del modal: quedaría diciendo que publica solo cuando no puede.
      if (!ig.conectada) return setModal('sin_cuenta');
      if (!ig.puedePublicar) return setModal('sin_permiso');
    }
    void guardarModo(nuevo);
  };

  const conectar = async () => {
    if (conectando) return;
    // ⚠️ La ventana se abre ACÁ, sincrónicamente dentro del click, antes de cualquier `await`
    // (ADR-2026-08-07-tiendanube-popup-safari).
    const popup = abrirVentanaDeInstagram();
    setConectando(true);
    setError(null);
    try {
      await conectarInstagramEnVentana(brandId, popup, { publicar: true });
      const nuevo = await estadoDeInstagram(brandId);
      // Conectó, pero no pudimos preguntar cómo quedó: no se pisa lo que había (con `error` el interruptor
      // desaparecería) y se avisa en el modal.
      if (nuevo.error) {
        setError('Conectamos tu Instagram, pero no pudimos confirmar el permiso. Probá de nuevo en un momento.');
        return;
      }
      setIg(nuevo);
      if (nuevo.conectada && nuevo.puedePublicar) {
        setModal(null);
        await guardarModo('automatico');
      } else {
        setModal(nuevo.conectada ? 'sin_permiso' : 'sin_cuenta');
        setError(SIN_PERMISO_TRAS_CONECTAR);
      }
    } catch (e) {
      if (!(e instanceof ErrorDeInstagram && e.code === 'ventana_cerrada')) {
        setError(e instanceof Error ? e.message : 'No se pudo conectar.');
      }
    } finally {
      setConectando(false);
    }
  };

  // Cargando: nada. Si no pudimos preguntar por Instagram, se muestra igual cuando está en automático, para
  // poder pausarlo (review Greptile #225); en "Avisarme" no hay nada que hacer sin saber cómo está.
  if (!visible || modo === null) return null;
  if ((!ig || ig.error) && modo !== 'automatico') return null;

  return (
    <>
      <ToggleDeModo modo={modo} onCambiar={cambiar} />
      <ModalConectarInstagram
        abierto={modal !== null}
        motivo={modal ?? 'sin_cuenta'}
        onCerrar={() => {
          setModal(null);
          setError(null);
        }}
        onConectar={() => void conectar()}
        conectando={conectando}
        error={error}
      />
    </>
  );
}
